import { useState, useEffect, useRef } from "react";
import "./CatchTheBalls.css";
import ActivityHeader from "../../components/ActivityHeader/ActivityHeader";
import ModalBox from "../../components/ModalBox/ModalBox";
import { initialModal, endGameModal } from "./Modals";
import { trackGameStart } from "../../utils/trackEvents";

const MAX_BALLS = 50;
const GAME_DURATION = 30;
const SPEED = 4.5;
const BUCKET_WIDTH = 160;

export default function CatchTheBalls() {
  const [balls, setBalls] = useState([]);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [ballsLeft, setBallsLeft] = useState(MAX_BALLS);
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [modalContent, setModalContent] = useState(initialModal());
  const [score, setScore] = useState(0);
  const [bucketPositions, setBucketPositions] = useState([]);
  const [popups, setPopups] = useState([]);

  const ballAreaRef = useRef(null);
  const lineRef = useRef(null);
  const bucketsRef = useRef(null);
  const rafRef = useRef(null);
  const ballRafRef = useRef(null);

  const handleClick = () => {
    if (!(isGameStarted && timeLeft > 0 && ballsLeft > 0)) return;
    const rect = ballAreaRef.current.getBoundingClientRect();
    const x = rect.left + rect.width / 2 - 10;
    const y = rect.top;
    setBalls((prev) => [...prev, { id: Date.now(), x, y }]);
    setBallsLeft((prev) => prev - 1);
  };

  useEffect(() => {
    if (!isGameStarted) return;

    const animateBalls = () => {
      setBalls((prev) => {
        const newBalls = [];
        const lineY =
          lineRef.current?.getBoundingClientRect().bottom || window.innerHeight;

        for (let ball of prev) {
          const newY = ball.y + 5;
          const ballTopY = newY + 20;

          const hit = bucketPositions.some(
            (left) =>
              ball.x >= left &&
              ball.x <= left + BUCKET_WIDTH &&
              ballTopY >= lineY - 40 &&
              ballTopY <= lineY - 35
          );

          if (hit) {
            setScore((s) => s + 1);
            const id = Date.now();
            setPopups((p) => [...p, { id, x: ball.x, y: newY }]);
            setTimeout(
              () => setPopups((p) => p.filter((pop) => pop.id !== id)),
              700
            );
            continue;
          }

          if (newY + 20 < lineY) newBalls.push({ ...ball, y: newY });
        }

        return newBalls;
      });

      ballRafRef.current = requestAnimationFrame(animateBalls);
    };

    animateBalls();
    return () => cancelAnimationFrame(ballRafRef.current);
  }, [isGameStarted, bucketPositions]);

  useEffect(() => {
    if (!isGameStarted) return;
    const timer = setInterval(
      () => setTimeLeft((t) => Math.max(t - 1, 0)),
      1000
    );
    return () => clearInterval(timer);
  }, [isGameStarted]);

  useEffect(() => {
    if (!isGameStarted) return;

    const updateBuckets = () => {
      const screenWidth = window.innerWidth;

      const count =
        Math.floor((screenWidth - BUCKET_WIDTH) / (BUCKET_WIDTH * 2.5)) + 1;

      const positions = Array.from(
        { length: count },
        (_, i) => i * BUCKET_WIDTH * 2.5
      );
      setBucketPositions(positions);
    };

    updateBuckets();
    window.addEventListener("resize", updateBuckets);

    const animate = () => {
      setBucketPositions((prev) =>
        prev.map((x) => {
          const screenWidth = window.innerWidth;
          const next = x + SPEED;
          return next > screenWidth ? -BUCKET_WIDTH : next;
        })
      );
      rafRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", updateBuckets);
    };
  }, [isGameStarted]);

  useEffect(() => {
    if (timeLeft === 0 || (ballsLeft === 0 && balls.length === 0)) {
      setIsGameStarted(false);
      setModalContent(endGameModal(score));
      setIsModalOpen(true);
    }
  }, [timeLeft, ballsLeft, balls.length, score]);

  const handleStart = () => {
    setIsModalOpen(false);
    setIsGameStarted(true);
    setTimeLeft(GAME_DURATION);
    setBallsLeft(MAX_BALLS);
    setBalls([]);
    setScore(0);
    setPopups([]);
    trackGameStart("CatchTheBalls", "DefaultMode");
  };

  return (
    <div className="catchTheBalls-main" onClick={handleClick}>
      <ActivityHeader>
        <h3>Time Left: {timeLeft}s</h3>
        <h3>Score: {score}</h3>
      </ActivityHeader>
      <ActivityHeader>
        <h3>Balls Left: {ballsLeft}</h3>
      </ActivityHeader>

      <div className="catchTheBalls-game">
        <div className="catchTheBalls-balls" ref={ballAreaRef}></div>
        <div className="catchTheBalls-line" ref={lineRef}>
          <div className="catchTheBalls-buckets-container" ref={bucketsRef}>
            {bucketPositions.map((left, i) => (
              <div
                key={i}
                className="bucket"
                style={{ left, top: 0, width: BUCKET_WIDTH }}
              />
            ))}
          </div>
          <div className="catchTheBalls-red-line"></div>
        </div>

        {balls.map((ball) => (
          <div
            key={ball.id}
            className="ball"
            style={{ left: `${ball.x}px`, top: `${ball.y}px` }}
          />
        ))}

        {popups.map((pop) => (
          <div
            key={pop.id}
            className="popup"
            style={{ left: `${pop.x}px`, top: `${pop.y}px` }}
          >
            +1
          </div>
        ))}
      </div>

      <ModalBox
        isOpen={isModalOpen}
        title={modalContent.title}
        description={modalContent.description}
        buttonText={modalContent.buttonText}
        buttonType={modalContent.buttonType}
        onClose={handleStart}
      />
    </div>
  );
}
